import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import { fmt } from "../endomarketing-utils";
import { horaCurta } from "../format";
import type { Row } from "../types";
import {
  CHECKLIST_CONDUCAO,
  ESCALA_ROTEIRO,
  MODALIDADES,
  RECOMENDACOES,
  ROTEIROS,
  blocosEmBranco,
  duracaoSugerida,
  mediaRoteiro,
  progressoRoteiro,
  roteiroPorKey,
  sugerirRoteiro,
  type BlocoPreenchido,
} from "../roteiro-entrevista";
import {
  IconClose,
  IconPlus,
  IconTrash,
  IconSparkles,
  IconCheck,
  IconStar,
  IconDownload,
  IconClipboard,
  IconEye,
} from "../icons";
import { usePodeExcluir } from "../permissoes";
import { CurriculoCandidatoPainel } from "./CurriculoCandidato";

/**
 * ROTEIRO DE ENTREVISTA — conduzir e MAPEAR a entrevista inteira.
 *
 * A tela é o próprio roteiro: blocos na ordem da conversa, o que observar em
 * cada pergunta, campo de anotação e nota de 1 a 5. No fim, o parecer com a
 * recomendação — que, se o RH quiser, já marca o status da entrevista.
 *
 * O roteiro é editado em memória e salvo de uma vez (como o plano de ação):
 * ninguém para no meio de uma entrevista para clicar em salvar.
 */

/** Rótulo dado à pergunta que o entrevistador anotou mas esqueceu de nomear. */
const SEM_TITULO = "Pergunta sem título (anotada na entrevista)";

/**
 * O roteiro como ele vai para o papel e para o banco.
 *
 * Pergunta em branco some — mas só quando está REALMENTE em branco. Antes o
 * filtro olhava apenas o título: quem escrevia a pergunta própria, anotava a
 * resposta e a nota e esquecia de dar um nome a ela perdia a anotação inteira,
 * sem nenhum aviso. Agora ela sobrevive com um rótulo, e a lista `renomeadas`
 * diz em que blocos isso aconteceu para a tela avisar.
 *
 * Fica FORA do componente de propósito: salvar e imprimir precisam do MESMO
 * resultado — quando só o salvar preservava, o papel continuava sem a anotação.
 */
function prepararParaRegistro(lista: BlocoPreenchido[]): {
  blocos: BlocoPreenchido[];
  renomeadas: string[];
} {
  const renomeadas: string[] = [];
  const blocos = lista.map((b) => ({
    ...b,
    perguntas: b.perguntas
      .filter((p) => p.texto.trim() || p.resposta.trim() || p.nota !== "")
      .map((p) => {
        if (p.texto.trim()) return p;
        renomeadas.push(b.titulo);
        return { ...p, texto: SEM_TITULO };
      }),
  }));
  return { blocos, renomeadas };
}

interface Props {
  entrevista: Row;
  onClose: () => void;
  onSalvo: (mensagem: string) => void | Promise<void>;
}

export default function RoteiroEntrevistaModal({ entrevista, onClose, onSalvo }: Props) {
  const entrevistaId = Number(entrevista.id);
  const podeExcluir = usePodeExcluir(); // só admin apaga o roteiro já gravado

  const [modelo, setModelo] = useState<string>(() => sugerirRoteiro(entrevista.cargo));
  // CARGO ESCRITO: o roteiro deixou de ser escolhido numa lista de tipos de vaga
  // e passou a ser montado a partir do cargo real, escrito aqui — é dele que a
  // IA tira as perguntas da função. A base (os blocos de trajetória,
  // comportamento, segurança…) continua sendo deduzida do que for escrito.
  const [cargoRoteiro, setCargoRoteiro] = useState<string>(String(entrevista.cargo ?? ""));
  const [gerandoRoteiro, setGerandoRoteiro] = useState(false);
  const [trocarBase, setTrocarBase] = useState(false);
  const [blocos, setBlocos] = useState<BlocoPreenchido[]>([]);
  const [marcados, setMarcados] = useState<string[]>([]);
  const [abertos, setAbertos] = useState<string[]>([]);

  const [modalidade, setModalidade] = useState<string>("Presencial");
  const [duracao, setDuracao] = useState<string>("");
  const [recomendacao, setRecomendacao] = useState<string>("");
  const [parecer, setParecer] = useState("");
  const [fortes, setFortes] = useState("");
  const [atencao, setAtencao] = useState("");
  const [pretensao, setPretensao] = useState("");
  const [inicio, setInicio] = useState("");
  const [proximoPasso, setProximoPasso] = useState("");
  const [aplicarStatus, setAplicarStatus] = useState(true);

  /**
   * CURRÍCULO AO LADO. Aberto por padrão: o motivo de existir este painel é ler
   * o currículo DURANTE a conversa — se viesse fechado, o entrevistador teria
   * de lembrar de abri-lo justo no momento em que está com o candidato na
   * frente. Em tela estreita o CSS empilha os dois (ver .rot-split).
   */
  const [verCurriculo, setVerCurriculo] = useState(true);

  const [salvo, setSalvo] = useState(false); // já existe roteiro gravado?
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [pensandoIa, setPensandoIa] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  /** Troca o modelo do roteiro (perde o que foi anotado — por isso confirma). */
  const aplicarModelo = useCallback((key: string) => {
    const novos = blocosEmBranco(key);
    setModelo(key);
    setBlocos(novos);
    setAbertos(novos.map((b) => b.id));
  }, []);

  /** Já tem anotação de entrevista feita? (o que refazer o roteiro apagaria) */
  function temAnotacao(): boolean {
    return blocos.some((b) => b.perguntas.some((p) => p.resposta.trim() || p.nota));
  }

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await api.roteiroEntrevista.obter(entrevistaId);
      if (r.roteiro && Array.isArray(r.roteiro.blocos) && r.roteiro.blocos.length > 0) {
        const rot = r.roteiro;
        setSalvo(true);
        setModelo(String(rot.modelo ?? "geral"));
        // Cargo escrito no roteiro; roteiro antigo (antes do campo) cai no
        // cargo da própria entrevista.
        setCargoRoteiro(String(rot.cargo ?? entrevista.cargo ?? ""));
        setBlocos(
          rot.blocos.map((b: any) => ({
            id: String(b.id ?? ""),
            titulo: String(b.titulo ?? ""),
            objetivo: String(b.objetivo ?? ""),
            minutos: Number(b.minutos) || 0,
            extra: b.extra === true,
            perguntas: (b.perguntas ?? []).map((p: any) => ({
              id: String(p.id ?? ""),
              texto: String(p.texto ?? ""),
              dica: p.dica ? String(p.dica) : undefined,
              competencia: p.competencia ? String(p.competencia) : undefined,
              resposta: String(p.resposta ?? ""),
              nota: p.nota == null ? "" : String(p.nota),
            })),
          })),
        );
        setAbertos(rot.blocos.map((b: any) => String(b.id)));
        setMarcados(Array.isArray(rot.checklist) ? rot.checklist.map(String) : []);
        setModalidade(String(rot.modalidade ?? "Presencial"));
        setDuracao(rot.duracao_min == null ? "" : String(rot.duracao_min));
        setRecomendacao(String(rot.recomendacao ?? ""));
        setParecer(String(rot.parecer ?? ""));
        setFortes(String(rot.pontos_fortes ?? ""));
        setAtencao(String(rot.pontos_atencao ?? ""));
        setPretensao(String(rot.pretensao_salarial ?? ""));
        setInicio(String(rot.disponibilidade_inicio ?? ""));
        setProximoPasso(String(rot.proximo_passo ?? ""));
      } else {
        // Entrevista sem roteiro: abre o modelo sugerido pelo cargo, em branco.
        const key = sugerirRoteiro(entrevista.cargo);
        setSalvo(false);
        setModelo(key);
        const novos = blocosEmBranco(key);
        setBlocos(novos);
        setAbertos(novos.map((b) => b.id));
      }
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao carregar o roteiro.");
    } finally {
      setCarregando(false);
    }
  }, [entrevistaId, entrevista.cargo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const media = useMemo(() => mediaRoteiro(blocos), [blocos]);
  const progresso = useMemo(() => progressoRoteiro(blocos), [blocos]);
  const minutos = useMemo(() => duracaoSugerida(blocos), [blocos]);

  function mudarPergunta(blocoId: string, perguntaId: string, campo: "resposta" | "nota", valor: string) {
    setBlocos((prev) =>
      prev.map((b) =>
        b.id !== blocoId
          ? b
          : {
              ...b,
              perguntas: b.perguntas.map((p) => (p.id === perguntaId ? { ...p, [campo]: valor } : p)),
            },
      ),
    );
  }

  function removerPergunta(blocoId: string, perguntaId: string) {
    setBlocos((prev) =>
      prev.map((b) =>
        b.id !== blocoId ? b : { ...b, perguntas: b.perguntas.filter((p) => p.id !== perguntaId) },
      ),
    );
  }

  /** Pergunta própria do entrevistador, dentro do bloco onde ele está. */
  function adicionarPergunta(blocoId: string) {
    setBlocos((prev) =>
      prev.map((b) =>
        b.id !== blocoId
          ? b
          : {
              ...b,
              perguntas: [
                ...b.perguntas,
                {
                  id: `${b.id}.x${b.perguntas.length + 1}-${Math.random().toString(36).slice(2, 7)}`,
                  texto: "",
                  competencia: "Pergunta do entrevistador",
                  resposta: "",
                  nota: "",
                },
              ],
            },
      ),
    );
    setAbertos((prev) => (prev.includes(blocoId) ? prev : [...prev, blocoId]));
  }

  function mudarTextoPergunta(blocoId: string, perguntaId: string, texto: string) {
    setBlocos((prev) =>
      prev.map((b) =>
        b.id !== blocoId
          ? b
          : { ...b, perguntas: b.perguntas.map((p) => (p.id === perguntaId ? { ...p, texto } : p)) },
      ),
    );
  }

  function alternarBloco(id: string) {
    setAbertos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function alternarChecklist(id: string) {
    setMarcados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  /**
   * MONTA O ROTEIRO DO CARGO ESCRITO.
   *
   * O entrevistador escreve o cargo real ("Assistente Administrativo de
   * Suprimentos") e a IA gera as perguntas DAQUELA função, que entram no lugar
   * do bloco técnico. Os outros blocos (trajetória, comportamento, cultura,
   * segurança, disponibilidade, remuneração) vêm da base deduzida do cargo e
   * continuam iguais para todo mundo — é o que deixa dois candidatos da mesma
   * vaga comparáveis.
   */
  async function gerarRoteiroDoCargo() {
    const cargo = cargoRoteiro.trim();
    if (!cargo) {
      setErro("Escreva o cargo da vaga para a IA montar as perguntas.");
      return;
    }
    if (
      temAnotacao() &&
      !confirm(
        "Gerar o roteiro deste cargo monta as perguntas de novo e apaga o que já foi anotado. Continuar?",
      )
    ) {
      return;
    }

    setGerandoRoteiro(true);
    setErro(null);
    setAviso(null);
    try {
      const base = sugerirRoteiro(cargo); // trajetória, comportamento, segurança…
      const novos = blocosEmBranco(base);

      const r = await api.roteiroEntrevista.perguntasIa(entrevistaId, {
        cargo,
        candidato: entrevista.candidato ?? "",
        etapa: entrevista.etapa ?? "",
      });
      const novas = (r.perguntas ?? []).filter((p) => String(p.texto ?? "").trim());

      // A IA entra NO LUGAR das perguntas técnicas genéricas. Se ela não trouxer
      // nada, as do catálogo continuam valendo — o roteiro nunca fica sem miolo.
      const comIa = novos.map((b) =>
        b.id !== "tecnico" || novas.length === 0
          ? b
          : {
              ...b,
              titulo: `3. Conhecimento técnico — ${cargo}`,
              objetivo: "Perguntas da função, geradas pela IA a partir do cargo. Revise antes de usar.",
              perguntas: novas.map((p, i) => ({
                id: `tecnico.ia${i + 1}`,
                texto: String(p.texto),
                dica: p.dica ? String(p.dica) : undefined,
                competencia: "Técnico da função",
                resposta: "",
                nota: "",
              })),
            },
      );

      setModelo(base);
      setBlocos(comIa);
      setAbertos(comIa.map((b) => b.id));
      setTrocarBase(false);
      setAviso(
        novas.length > 0
          ? `Roteiro de "${cargo}" montado — ${novas.length} pergunta(s) da função. Revise e ajuste o que quiser.`
          : `Roteiro montado com o modelo "${roteiroPorKey(base).nome}". A IA não trouxe perguntas desta vez; tente de novo ou escreva as suas.`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar o roteiro do cargo.");
    } finally {
      setGerandoRoteiro(false);
    }
  }

  /** Perguntas técnicas da função sugeridas pela IA, num bloco à parte. */
  async function sugerirComIa() {
    setPensandoIa(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await api.roteiroEntrevista.perguntasIa(entrevistaId, {
        // O cargo escrito manda: é ele que descreve a vaga de verdade.
        cargo: cargoRoteiro.trim() || String(entrevista.cargo ?? ""),
        candidato: entrevista.candidato ?? "",
        etapa: entrevista.etapa ?? "",
      });
      const novas = (r.perguntas ?? []).filter((p) => String(p.texto ?? "").trim());
      if (novas.length === 0) {
        setAviso("A IA não trouxe perguntas desta vez. Tente de novo ou escreva a pergunta à mão.");
        return;
      }
      const idBloco = "ia";
      // O descarte das repetidas acontece DENTRO do setBlocos, contra o estado
      // do momento (`prev`): entre o clique e a resposta da IA passam vários
      // segundos, e o outro botão de IA pode ter acrescentado perguntas nesse
      // meio-tempo — comparar com o `blocos` do closure deixaria entrar
      // duplicata. O quanto entrou é anotado aqui para o aviso poder dizer a
      // verdade (antes ele anunciava o que a IA devolveu, não o que entrou).
      let acrescentadas = 0;

      setBlocos((prev) => {
        const jaTem = new Set(
          prev.flatMap((b) => b.perguntas).map((p) => p.texto.trim().toLowerCase()),
        );
        const perguntas = novas
          .filter((p) => !jaTem.has(String(p.texto).trim().toLowerCase()))
          .map((p, i) => ({
            id: `${idBloco}.${Date.now().toString(36)}${i}`,
            texto: String(p.texto),
            dica: p.dica ? String(p.dica) : undefined,
            competencia: "Técnico da função",
            resposta: "",
            nota: "",
          }));
        acrescentadas = perguntas.length;
        if (perguntas.length === 0) return prev;

        const existente = prev.find((b) => b.id === idBloco);
        if (existente) {
          return prev.map((b) =>
            b.id === idBloco ? { ...b, perguntas: [...b.perguntas, ...perguntas] } : b,
          );
        }
        // Entra logo depois do bloco técnico (3), que é o assunto dele.
        const novoBloco: BlocoPreenchido = {
          id: idBloco,
          titulo: `Perguntas da função — ${cargoRoteiro.trim() || entrevista.cargo || "vaga"} (IA)`,
          objetivo:
            "Sugeridas pela IA a partir do cargo. Revise, apague o que não cabe e acrescente o que faltar.",
          minutos: 10,
          extra: true,
          perguntas,
        };
        const pos = prev.findIndex((b) => b.id === "tecnico");
        const copia = [...prev];
        copia.splice(pos >= 0 ? pos + 1 : copia.length, 0, novoBloco);
        return copia;
      });
      if (acrescentadas === 0) {
        setAviso("As perguntas que a IA trouxe já estão no roteiro — nada foi acrescentado.");
        return;
      }
      setAbertos((prev) => (prev.includes(idBloco) ? prev : [...prev, idBloco]));
      const repetidas = novas.length - acrescentadas;
      setAviso(
        `${acrescentadas} pergunta(s) acrescentada(s)` +
          (repetidas > 0 ? ` (${repetidas} já estavam no roteiro)` : "") +
          " — revise antes de usar na entrevista.",
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível sugerir as perguntas.");
    } finally {
      setPensandoIa(false);
    }
  }

  async function salvar() {
    setErro(null);
    if (blocos.length === 0) {
      setErro("Escolha um modelo de roteiro antes de salvar.");
      return;
    }
    const { blocos: prontos, renomeadas } = prepararParaRegistro(blocos);

    setSalvando(true);
    try {
      const r = await api.roteiroEntrevista.salvar(entrevistaId, {
        modelo,
        modelo_nome: roteiroPorKey(modelo).nome,
        // O cargo escrito é o que identifica o roteiro daqui em diante.
        cargo: cargoRoteiro.trim(),
        blocos: prontos,
        checklist: marcados,
        modalidade,
        duracao_min: duracao === "" ? null : Number(duracao),
        recomendacao,
        parecer,
        pontos_fortes: fortes,
        pontos_atencao: atencao,
        pretensao_salarial: pretensao,
        disponibilidade_inicio: inicio,
        proximo_passo: proximoPasso,
        aplicar_status: aplicarStatus,
      });
      setSalvo(true);
      // A tela passa a mostrar o que foi GRAVADO (com a pergunta já renomeada),
      // senão ela seguiria exibindo o campo em branco e a próxima impressão
      // discordaria do que está no banco.
      setBlocos(prontos);
      // Guardamos a anotação e avisamos: o entrevistador volta e dá o nome
      // certo à pergunta, em vez de descobrir depois que o registro sumiu.
      const extra = renomeadas.length
        ? ` ${renomeadas.length} pergunta(s) ficaram sem título e foram salvas como "Pergunta sem título" — dê um nome a elas em ${[...new Set(renomeadas)].join(", ")}.`
        : "";
      await onSalvo((r.mensagem ?? "Roteiro salvo.") + extra);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao salvar o roteiro.");
      setSalvando(false);
    }
  }

  async function excluir() {
    if (!confirm("Apagar o roteiro preenchido desta entrevista?")) return;
    try {
      await api.roteiroEntrevista.remover(entrevistaId);
      await onSalvo("Roteiro removido.");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao remover o roteiro.");
    }
  }

  /**
   * Versão para papel. Serve para as duas coisas: imprimir ANTES, em branco,
   * para conduzir a entrevista com o roteiro na mão (as respostas viram linhas
   * pautadas), e imprimir DEPOIS, preenchido, para arquivar com o parecer.
   */
  function imprimir() {
    // Mesmo preparo do salvamento: a pergunta anotada sem título tem de sair no
    // papel também — era ela que o entrevistador levava para a reunião.
    const secoes = prepararParaRegistro(blocos)
      .blocos.map((b) => {
        const linhas = b.perguntas
          .map(
            (p, i) => `<tr>
              <td class="num">${i + 1}</td>
              <td>
                <strong>${escapar(p.texto)}</strong>
                ${p.dica ? `<div class="peq">Observar: ${escapar(p.dica)}</div>` : ""}
                ${
                  p.resposta.trim()
                    ? `<div class="resp">${escapar(p.resposta)}</div>`
                    : `<div class="pauta"></div><div class="pauta"></div>`
                }
              </td>
              <td class="nota">${p.nota ? `${p.nota}/5` : "___"}</td>
            </tr>`,
          )
          .join("");
        if (!linhas) return "";
        return `<h2>${escapar(b.titulo)} <span class="min">${b.minutos} min</span></h2>
          <p class="obj">${escapar(b.objetivo)}</p>
          <table><tbody>${linhas}</tbody></table>`;
      })
      .join("");

    const checklistHtml = ["Antes", "Durante", "Depois"]
      .map((momento) => {
        const itens = CHECKLIST_CONDUCAO.filter((c) => c.momento === momento)
          .map(
            (c) =>
              `<li>${marcados.includes(c.id) ? "☑" : "☐"} ${escapar(c.texto)}</li>`,
          )
          .join("");
        return `<div><strong>${momento}</strong><ul class="check">${itens}</ul></div>`;
      })
      .join("");

    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Roteiro de entrevista — ${escapar(String(entrevista.candidato ?? ""))}</title>
      <style>
        *{box-sizing:border-box} body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:26px}
        h1{font-size:19px;margin:0 0 4px}
        h2{font-size:13px;margin:16px 0 4px;color:#0c7a3f;display:flex;gap:8px;align-items:baseline}
        .min{font-size:10.5px;color:#5e7268;font-weight:400}
        .cab{border-bottom:2px solid #0c7a3f;padding-bottom:10px;margin-bottom:12px}
        .linha{font-size:12.5px;color:#444;margin:2px 0}
        .obj{font-size:11px;color:#5e7268;margin:0 0 4px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        td{border:1px solid #cbd8d0;padding:6px 8px;vertical-align:top}
        .num{width:26px;text-align:center;color:#5e7268}
        .nota{width:52px;text-align:center;font-weight:700}
        .peq{font-size:10.5px;color:#5e7268;margin-top:2px}
        .resp{font-size:12px;margin-top:5px;white-space:pre-wrap;background:#f4f9f6;padding:5px 7px;border-radius:4px}
        .pauta{border-bottom:1px dotted #9db3a6;height:15px;margin-top:6px}
        .grade{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;font-size:11.5px;margin:10px 0}
        .check{margin:4px 0 0;padding-left:2px;list-style:none;font-size:11px;line-height:1.6}
        .txt{font-size:12.5px;line-height:1.5;white-space:pre-wrap;margin:0 0 8px;
             border:1px solid #cbd8d0;border-radius:5px;padding:7px 9px;min-height:34px}
        .ass{display:flex;gap:40px;margin-top:44px}
        .ass div{flex:1;border-top:1px solid #333;padding-top:6px;font-size:11.5px;text-align:center}
        @media print{body{margin:11mm} h2{page-break-after:avoid} table{page-break-inside:auto}
          tr{page-break-inside:avoid}}
      </style></head><body>
      <div class="cab">
        <h1>Roteiro de entrevista</h1>
        <p class="linha"><strong>${escapar(String(entrevista.candidato ?? "—"))}</strong> · ${escapar(
          cargoRoteiro.trim() || String(entrevista.cargo ?? "cargo não informado"),
        )}</p>
        <p class="linha">${entrevista.data ? fmt(String(entrevista.data)) : "data: ____/____/______"}${
          horaCurta(entrevista.horario) ? ` às ${horaCurta(entrevista.horario)}` : ""
        } ·
          Etapa: ${escapar(String(entrevista.etapa ?? "—"))} ·
          Entrevistador: ${escapar(String(entrevista.entrevistador ?? "________________"))} ·
          ${escapar(modalidade)}</p>
        <p class="linha">Roteiro: ${escapar(
          cargoRoteiro.trim() ? `${cargoRoteiro.trim()} (base: ${roteiroPorKey(modelo).nome})` : roteiroPorKey(modelo).nome,
        )} · Tempo previsto: ${minutos} min ·
          Nota geral: ${media != null ? `${media.toFixed(1)}/5` : "____"}
          ${recomendacao ? ` · Parecer: ${escapar(recomendacao)}` : ""}</p>
      </div>

      <h2>Condução</h2>
      <div class="grade">${checklistHtml}</div>

      ${secoes}

      <h2>Parecer do entrevistador</h2>
      <p class="linha">Pontos fortes</p><div class="txt">${escapar(fortes)}</div>
      <p class="linha">Pontos de atenção</p><div class="txt">${escapar(atencao)}</div>
      <p class="linha">Resumo / conclusão</p><div class="txt">${escapar(parecer)}</div>
      <p class="linha">Pretensão salarial: ${escapar(pretensao || "____________")} ·
        Disponibilidade para início: ${escapar(inicio || "____________")} ·
        Próximo passo: ${escapar(proximoPasso || "____________")}</p>

      <div class="ass"><div>Entrevistador</div><div>Gestor da área</div><div>RH</div></div>
      </body></html>`;

    const janela = window.open("", "_blank");
    if (!janela) {
      setErro("O navegador bloqueou a janela de impressão. Libere os pop-ups deste site.");
      return;
    }
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    janela.print();
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className={verCurriculo ? "modal-card rot-card com-curriculo" : "modal-card rot-card"}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <h3>
              <IconClipboard size={18} /> Roteiro de entrevista
            </h3>
            <p className="modal-sub">
              {String(entrevista.candidato ?? "candidato")} ·{" "}
              {String(entrevista.cargo || "cargo não informado")}
              {entrevista.data ? ` · ${fmt(String(entrevista.data))}` : ""}
              {horaCurta(entrevista.horario) ? ` às ${horaCurta(entrevista.horario)}` : ""}
              {entrevista.etapa ? ` · ${entrevista.etapa}` : ""}
            </p>
          </div>
          <div className="rot-head-acoes">
            {/* O currículo fica ao lado do roteiro: dá para ler a experiência
                da pessoa enquanto a pergunta está sendo feita, sem trocar de
                aba nem perder o que já foi anotado. */}
            <button
              type="button"
              className={verCurriculo ? "btn-secondary btn-sm ativo" : "btn-secondary btn-sm"}
              onClick={() => setVerCurriculo((v) => !v)}
              title="Currículo, respostas da triagem e DISC, ao lado do roteiro"
            >
              <IconEye size={15} /> {verCurriculo ? "Esconder currículo" : "Ver currículo"}
            </button>
            <button type="button" className="icon-btn" onClick={onClose}>
              <IconClose size={18} />
            </button>
          </div>
        </div>

        <div className="rot-split">
        <div className="modal-body">
          {carregando ? (
            <div className="empty">Carregando o roteiro…</div>
          ) : (
            <>
              {/* ---- Barra do roteiro: modelo, progresso e nota ---- */}
              <div className="rot-topo">
                {/* O roteiro é ESCRITO: o cargo real da vaga, e a IA monta as
                    perguntas dele. A base (blocos comuns) sai do que for
                    escrito, e continua trocável para quem quiser mandar nela. */}
                <label className="field rot-modelo">
                  <span>Roteiro — cargo da vaga</span>
                  <div className="rot-cargo-linha">
                    <input
                      value={cargoRoteiro}
                      onChange={(e) => setCargoRoteiro(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          gerarRoteiroDoCargo();
                        }
                      }}
                      placeholder="Escreva o cargo (ex.: Assistente Administrativo de Suprimentos)"
                    />
                    <button
                      type="button"
                      className="btn-ia btn-sm"
                      onClick={gerarRoteiroDoCargo}
                      disabled={gerandoRoteiro || pensandoIa || !cargoRoteiro.trim()}
                      title={
                        cargoRoteiro.trim()
                          ? "A IA monta as perguntas deste cargo"
                          : "Escreva o cargo da vaga primeiro"
                      }
                    >
                      <IconSparkles size={15} />{" "}
                      {gerandoRoteiro ? "Gerando…" : "Gerar perguntas do cargo"}
                    </button>
                  </div>
                  <small className="muted rot-base">
                    Escreva o cargo correto e clique em gerar — as perguntas saem de acordo com a
                    função. Base: <strong>{roteiroPorKey(modelo).nome}</strong>{" "}
                    <button
                      type="button"
                      className="rot-link"
                      onClick={() => setTrocarBase((v) => !v)}
                    >
                      {trocarBase ? "fechar" : "trocar"}
                    </button>
                  </small>
                  {trocarBase && (
                    <select
                      value={modelo}
                      onChange={(e) => {
                        const key = e.target.value;
                        if (
                          temAnotacao() &&
                          !confirm(
                            "Trocar a base monta as perguntas do novo modelo e apaga o que já foi anotado. Continuar?",
                          )
                        ) {
                          return;
                        }
                        aplicarModelo(key);
                      }}
                    >
                      {ROTEIROS.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.nome}
                        </option>
                      ))}
                    </select>
                  )}
                </label>

                <label className="field rot-campo-curto">
                  <span>Modalidade</span>
                  <select value={modalidade} onChange={(e) => setModalidade(e.target.value)}>
                    {MODALIDADES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field rot-campo-curto">
                  <span>Duração (min)</span>
                  <input
                    type="number"
                    min={0}
                    max={600}
                    value={duracao}
                    placeholder={String(minutos)}
                    onChange={(e) => setDuracao(e.target.value)}
                  />
                </label>

                <div className="rot-indicadores">
                  <span className="rot-nota">
                    <IconStar size={15} /> {media != null ? media.toFixed(1) : "—"}
                    <small>de 5</small>
                  </span>
                  <span className="muted">
                    {progresso.feitas} de {progresso.total} pergunta(s) · tempo previsto {minutos} min
                  </span>
                  <div className="endo-barra">
                    <div className="endo-barra-fill" style={{ width: `${progresso.pct}%` }} />
                  </div>
                </div>
              </div>

              <div className="rot-escala">
                <strong>Nota de cada resposta:</strong>
                {ESCALA_ROTEIRO.map((n) => (
                  <span key={n.valor} title={n.descricao}>
                    <b>{n.valor}</b> {n.rotulo}
                  </span>
                ))}
              </div>

              {aviso && (
                <div className="endo-aviso">
                  <span>{aviso}</span>
                  <button className="icon-btn" title="Fechar" onClick={() => setAviso(null)}>
                    <IconClose size={16} />
                  </button>
                </div>
              )}

              {/* ---- Checklist de condução ---- */}
              <details className="rot-checklist">
                <summary>
                  <IconCheck size={14} /> Condução da entrevista — antes, durante e depois (
                  {marcados.length}/{CHECKLIST_CONDUCAO.length})
                </summary>
                <div className="rot-checklist-grade">
                  {(["Antes", "Durante", "Depois"] as const).map((momento) => (
                    <div key={momento}>
                      <strong>{momento}</strong>
                      {CHECKLIST_CONDUCAO.filter((c) => c.momento === momento).map((c) => (
                        <label
                          key={c.id}
                          className={`rot-check-item ${marcados.includes(c.id) ? "marcado" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={marcados.includes(c.id)}
                            onChange={() => alternarChecklist(c.id)}
                          />
                          <span>{c.texto}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </details>

              <div className="rot-acoes-topo">
                <button
                  className="btn-secondary btn-sm"
                  onClick={sugerirComIa}
                  disabled={pensandoIa || gerandoRoteiro}
                  title="Acrescenta mais perguntas do cargo, num bloco à parte, sem apagar o que já está no roteiro"
                >
                  <IconSparkles size={15} />{" "}
                  {pensandoIa ? "Pensando…" : "Mais perguntas da função (IA)"}
                </button>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => setAbertos(abertos.length ? [] : blocos.map((b) => b.id))}
                >
                  {abertos.length ? "Recolher blocos" : "Abrir blocos"}
                </button>
              </div>

              {/* ---- Os blocos da entrevista ---- */}
              <div className="rot-blocos">
                {blocos.map((bloco) => {
                  const aberto = abertos.includes(bloco.id);
                  const feitas = bloco.perguntas.filter(
                    (p) => p.resposta.trim() || p.nota,
                  ).length;
                  return (
                    <section key={bloco.id} className={`rot-bloco ${aberto ? "aberto" : ""}`}>
                      <header onClick={() => alternarBloco(bloco.id)}>
                        <div className="rot-bloco-titulo">
                          <strong>{bloco.titulo}</strong>
                          <small>{bloco.objetivo}</small>
                        </div>
                        <span className="badge">
                          {feitas}/{bloco.perguntas.length}
                        </span>
                        <span className="rot-min">{bloco.minutos} min</span>
                      </header>

                      {aberto && (
                        <div className="rot-perguntas">
                          {bloco.perguntas.map((p, i) => (
                            <div className="rot-pergunta" key={p.id}>
                              <span className="rot-num">{i + 1}</span>
                              <div className="rot-pergunta-corpo">
                                {p.competencia === "Pergunta do entrevistador" ? (
                                  <input
                                    className="rot-pergunta-texto"
                                    value={p.texto}
                                    placeholder="Escreva a sua pergunta"
                                    onChange={(e) =>
                                      mudarTextoPergunta(bloco.id, p.id, e.target.value)
                                    }
                                  />
                                ) : (
                                  <strong>{p.texto}</strong>
                                )}
                                {p.dica && <small className="rot-dica">Observar: {p.dica}</small>}
                                <textarea
                                  rows={2}
                                  className="rot-resposta"
                                  value={p.resposta}
                                  placeholder="Anote a resposta do candidato…"
                                  onChange={(e) =>
                                    mudarPergunta(bloco.id, p.id, "resposta", e.target.value)
                                  }
                                />
                              </div>
                              <div className="rot-lateral">
                                {p.competencia && (
                                  <span className="rot-competencia">{p.competencia}</span>
                                )}
                                <div className="rot-notas">
                                  {ESCALA_ROTEIRO.map((n) => (
                                    <button
                                      key={n.valor}
                                      type="button"
                                      className={`rot-nota-btn ${
                                        String(n.valor) === p.nota ? "marcada" : ""
                                      }`}
                                      title={`${n.valor} — ${n.rotulo}: ${n.descricao}`}
                                      onClick={() =>
                                        mudarPergunta(
                                          bloco.id,
                                          p.id,
                                          "nota",
                                          String(n.valor) === p.nota ? "" : String(n.valor),
                                        )
                                      }
                                    >
                                      {n.valor}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  className="icon-btn icon-btn-danger"
                                  title="Tirar esta pergunta do roteiro"
                                  onClick={() => removerPergunta(bloco.id, p.id)}
                                >
                                  <IconTrash size={15} />
                                </button>
                              </div>
                            </div>
                          ))}

                          <button
                            className="btn-secondary btn-sm rot-add"
                            onClick={() => adicionarPergunta(bloco.id)}
                          >
                            <IconPlus size={14} /> Acrescentar pergunta neste bloco
                          </button>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>

              {/* ---- Parecer ---- */}
              <section className="rot-parecer">
                <h4>Parecer do entrevistador</h4>
                <div className="form-grid">
                  <label className="field">
                    <span>Recomendação</span>
                    <select value={recomendacao} onChange={(e) => setRecomendacao(e.target.value)}>
                      <option value="">— escolher —</option>
                      {RECOMENDACOES.map((r) => (
                        <option key={r.valor} value={r.valor}>
                          {r.valor}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Próximo passo</span>
                    <input
                      value={proximoPasso}
                      placeholder="Ex.: entrevista com o gestor em 12/08"
                      onChange={(e) => setProximoPasso(e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Pretensão salarial</span>
                    <input
                      value={pretensao}
                      placeholder="Ex.: R$ 3.200,00"
                      onChange={(e) => setPretensao(e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Disponibilidade para início</span>
                    <input
                      value={inicio}
                      placeholder="Ex.: imediata / 30 dias de aviso"
                      onChange={(e) => setInicio(e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Pontos fortes</span>
                    <textarea rows={2} value={fortes} onChange={(e) => setFortes(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Pontos de atenção</span>
                    <textarea rows={2} value={atencao} onChange={(e) => setAtencao(e.target.value)} />
                  </label>
                  <label className="field field-wide">
                    <span>Resumo / conclusão da entrevista</span>
                    <textarea
                      rows={3}
                      value={parecer}
                      placeholder="O que ficou claro na conversa e por que a recomendação é essa."
                      onChange={(e) => setParecer(e.target.value)}
                    />
                  </label>
                </div>

                <label className="rot-aplicar">
                  <input
                    type="checkbox"
                    checked={aplicarStatus}
                    onChange={(e) => setAplicarStatus(e.target.checked)}
                  />
                  <span>
                    Atualizar o status da entrevista com a recomendação
                    {recomendacao && (
                      <>
                        {" "}
                        (ficará como{" "}
                        <strong>
                          {RECOMENDACOES.find((r) => r.valor === recomendacao)?.status ?? "—"}
                        </strong>
                        )
                      </>
                    )}
                  </span>
                </label>
              </section>
            </>
          )}
        </div>

          {verCurriculo && (
            <aside className="rot-curriculo">
              <CurriculoCandidatoPainel entrevista={entrevista} onMudou={() => {}} />
            </aside>
          )}
        </div>

        <div className="modal-foot rot-foot">
          {erro && <div className="form-error">{erro}</div>}
          <button type="button" className="btn-secondary" onClick={imprimir} disabled={carregando}>
            <IconDownload size={16} /> Imprimir
          </button>
          {salvo && podeExcluir && (
            <button type="button" className="btn-secondary btn-danger-text" onClick={excluir}>
              <IconTrash size={16} /> Apagar roteiro
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={salvar}
            disabled={salvando || carregando}
          >
            {salvando ? "Salvando…" : "Salvar roteiro"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Escapa o texto que vai para a janela de impressão (é HTML montado à mão). */
function escapar(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
